/**
 * Tile-level diff between a saved collage and the version replacing it on SAVE.
 *
 * The counts feed the `driftMyAuraColor` Cloud Function, which decides how far and in
 * which direction the owner's aura color moves. Only the counts are computed here — the
 * weighting and the color math stay server-side for the same reason aura assignment does
 * (see functions/index.js).
 */

/**
 * Swag/place tiles carry a stable catalog `id`, while `imageUrl` is rewritten every time
 * the image is re-uploaded to Storage, so identity must not depend on the URL.
 */
function tileIdentity(tile) {
  if (!tile) return '';
  if (typeof tile === 'string') return tile.trim();
  if (typeof tile !== 'object') return String(tile);
  const id = typeof tile.id === 'string' ? tile.id.trim() : '';
  if (id) return id;
  const name = typeof tile.name === 'string' ? tile.name.trim() : '';
  if (name) return name;
  return typeof tile.imageUrl === 'string' ? tile.imageUrl.trim() : '';
}

/** Tiles are positional slots in the collage, so compare index-by-index. */
function countSlotChanges(oldList, newList) {
  const prev = Array.isArray(oldList) ? oldList : [];
  const next = Array.isArray(newList) ? newList : [];
  let changed = 0;
  for (let i = 0; i < Math.max(prev.length, next.length); i += 1) {
    if (tileIdentity(prev[i]) !== tileIdentity(next[i])) changed += 1;
  }
  return changed;
}

function textIdentity(value) {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  return String(value);
}

const EMOJI_SLOT_FIELDS = ['activityX', 'activityY', 'activityZ'];

/**
 * Count how many tiles changed between two poster docs, grouped by category.
 * Both arguments should be poster docs in `buildPosterDoc` shape.
 */
export function countCollageTileChanges(oldPoster, newPoster) {
  const prev = oldPoster && typeof oldPoster === 'object' ? oldPoster : {};
  const next = newPoster && typeof newPoster === 'object' ? newPoster : {};

  const emojis = EMOJI_SLOT_FIELDS.reduce(
    (total, field) => total + (textIdentity(prev[field]) !== textIdentity(next[field]) ? 1 : 0),
    0,
  );

  const gifs = textIdentity(prev.heartPhotoUrl) !== textIdentity(next.heartPhotoUrl) ? 1 : 0;

  /* A photo re-upload replaces the masked photo and the cutout together — one change, not two. */
  const photoChanged =
    textIdentity(prev.userPhotoUrl) !== textIdentity(next.userPhotoUrl)
    || textIdentity(prev.partnerCutout) !== textIdentity(next.partnerCutout);

  return {
    swags: countSlotChanges(prev.swags, next.swags),
    places: countSlotChanges(prev.places, next.places),
    emojis,
    gifs,
    photo: photoChanged ? 1 : 0,
  };
}

export function hasCollageTileChanges(changes) {
  if (!changes || typeof changes !== 'object') return false;
  return Object.values(changes).some((count) => typeof count === 'number' && count > 0);
}
